import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, RefreshCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CharacterGrid } from "../components/CharacterGrid";
import { ImageWithOverlay } from "../components/ImageWithOverlay";
import { useImageSize } from "../hooks/useImageSize";
import { useSessionResult } from "../queries/useRecords";
import { cn, formatDate } from "../lib/utils";
import type { CharacterResult } from "../types/domain";

export function ResultPage() {
  const { sessionId = "" } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const query = useSessionResult(sessionId);
  const size = useImageSize(query.data?.imageUrl);

  // `useWeakestCharacter` is a hook that must run unconditionally every render;
  // calling it AFTER an early return (e.g. when `query.data` was just loaded)
  // causes React error #310. Hoist the hook here.
  const characters = query.data?.characters ?? [];
  const topWeak = useWeakestCharacter(characters);

  if (query.isLoading) {
    return <div className="text-sm text-[color:var(--text-muted)]">加载中…</div>;
  }

  if (query.isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <h1 className="text-xl font-semibold">加载失败</h1>
        <p className="text-sm text-[color:var(--accent)]">{query.error?.message}</p>
        <Button variant="outline" onClick={() => query.refetch()}>重试</Button>
      </div>
    );
  }

  if (!query.data) {
    return (
      <div className="flex flex-col items-start gap-3">
        <h1 className="text-xl font-semibold">未找到该会话</h1>
        <p className="text-sm text-[color:var(--text-muted)]">
          会话 ID：<code className="rounded bg-[color:var(--surface-2)] px-1.5 py-0.5">{sessionId}</code>
          ，可能尚未同步或会话已过期。
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/history")}>查看历史</Button>
          <Button onClick={() => query.refetch()}>刷新</Button>
        </div>
      </div>
    );
  }

  const { imageUrl, overall } = query.data;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--text-muted)] underline-offset-4 hover:text-[color:var(--text)] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          返回首页
        </Link>
        <span className="text-xs text-[color:var(--text-muted)]">{formatDate(new Date())}</span>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-baseline gap-6">
            <div>
              <div className="text-xs text-[color:var(--text-muted)]">总平均分</div>
              <div className="text-4xl font-bold tabular-nums">{Math.round(overall.totalScore)}</div>
            </div>
            <div>
              <div className="text-xs text-[color:var(--text-muted)]">字数</div>
              <div className="text-2xl font-semibold tabular-nums">{overall.total}</div>
            </div>
            <div>
              <div className="text-xs text-[color:var(--text-muted)]">薄弱字数</div>
              <div
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold tabular-nums",
                  overall.weakCount > 0
                    ? "bg-[color:var(--accent)]/10 text-[color:var(--accent)]"
                    : "bg-[color:var(--good)]/10 text-[color:var(--good)]",
                )}
              >
                {overall.weakCount}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[color:var(--text-muted)]">{summarize(overall, characters)}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>原图标注</CardTitle>
        </CardHeader>
        <CardContent>
          {!imageUrl ? (
            <div className="rounded-md border border-dashed border-[color:var(--border)] px-4 py-6 text-center text-sm text-[color:var(--text-muted)]">
              原图未生成。需要配置 R2 API 凭证(
              <code className="rounded bg-[color:var(--surface-2)] px-1 py-0.5 text-xs">wrangler secret put R2_ACCESS_KEY_ID</code>
              等)才能下载到签名链接。原图已存入 R2，但默认私有不可直接显示。
            </div>
          ) : size.width > 0 && size.height > 0 ? (
            <ImageWithOverlay
              imageUrl={imageUrl}
              imageWidth={size.width}
              imageHeight={size.height}
              characters={characters.map((c) => ({
                char: c.char,
                bbox: c.bbox,
                score: c.totalScore,
                recordId: c.recordId,
              }))}
              interactive
              onSelect={(recordId) => navigate(`/overlay/${recordId}`)}
            />
          ) : (
            <div className="text-sm text-[color:var(--text-muted)]">图片加载中…</div>
          )}
          <p className="mt-3 text-xs text-[color:var(--text-muted)]">
            悬停查看字符与分数；颜色：红 &lt; 60 · 橙 &lt; 75 · 绿 ≥ 75
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>逐字打分</CardTitle>
        </CardHeader>
        <CardContent>
          <CharacterGrid
            characters={characters}
            onSelect={(recordId) => navigate(`/overlay/${recordId}`)}
          />
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3">
        <Button onClick={() => navigate("/")}>
          <RefreshCcw className="h-4 w-4" />
          再练一张
        </Button>
        {topWeak && (
          <Button
            variant="outline"
            onClick={() => navigate(`/overlay/${topWeak.recordId}`)}
          >
            查看单字详情
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function useWeakestCharacter(chars: CharacterResult[]): CharacterResult | null {
  return useMemo(() => {
    if (chars.length === 0) return null;
    const sorted = [...chars].sort((a, b) => a.totalScore - b.totalScore);
    return sorted[0] ?? null;
  }, [chars]);
}

function summarize(
  overall: { totalScore: number; weakCount: number; total: number },
  chars: CharacterResult[],
): string {
  if (chars.length === 0) return "本次会话未识别出任何字符。";
  const passCount = chars.length - overall.weakCount;
  const sorted = [...chars].sort((a, b) => a.totalScore - b.totalScore);
  const worst = sorted[0];
  const parts: string[] = [];
  parts.push(`${passCount}/${chars.length} 字在合格线以上。`);
  if (worst && overall.weakCount > 0) {
    parts.push(
      `重点关注「${worst.char}」，本次得分 ${Math.round(worst.totalScore)} 分。`,
    );
  } else {
    parts.push("整体表现良好，继续保持！");
  }
  return parts.join(" ");
}